<template>
  <div :class="canEdit ? 'admin' : 'user'">
    <div class="page-header">
      <h3 class="flex-grow-1" v-html="title ? title : $t('printables')"/>

      <div class="btn-group mr-2">
        <button :disabled="checkedRows.length == 0 || creatingDownload"
                type="button"
                class="btn btn-secondary dropdown-toggle"
                data-toggle="dropdown" aria-haspopup="true"
                aria-expanded="false"
        >
          {{ creatingDownload ? "Skapar nedladdning" : "Med " + checkedRows.length + " markerade" }}
        </button>
        <div class="dropdown-menu">
          <a v-if="canEdit" class="dropdown-item" href="#" @click="makeExport(false)">Ladda ner tryckfiler</a>
          <a class="dropdown-item" href="#" @click="makeExport(true)">Ladda ner förhandsgranskningar</a>
          <a class="dropdown-item" href="#" @click="openDeadlineModal()">Ändra deadline</a>
          <a class="dropdown-item" href="#" @click="openAuditorModal()">Ändra granskare</a>
          <a class="dropdown-item" href="#" @click="openExternAuditorModal()">Ändra externa granskare</a>
          <button :disabled="disableExport" class="dropdown-item" href="#" @click="openExportModal()"> Ändra exporteringsdatum</button>
          <button :disabled="disableDelivery" class="dropdown-item" href="#" @click="openDeliveryModal()">Ändra slutleveransdatum</button>
          <!--<div class="dropdown-divider"></div>
            <a class="dropdown-item" href="#">Godkänn</a>
            <a class="dropdown-item" href="#">Återställ status</a>-->
        </div>
      </div>

      <div class="btn-group mr-2">
        <button :class="{active : view == 'list'}" class="btn btn-secondary" @click="view = 'list'"><fa :icon="['fas','list']" /> </button>
        <button :class="{active : view == 'thumbs'}" class="btn btn-secondary" @click="view = 'thumbs'"><fa :icon="['fas','th-large']" /></button>
      </div>

      <router-link v-if="canEdit"
                   :to="{name : 'printables.create'}"
                   class="btn btn-success">
        <fa :icon="['fal','plus-circle']" /> {{ $t('add_new_printable') }}
      </router-link>
    </div>

    <div class="card mb-3">
      <div class="card-header">
        <h5 class="m-0">Filtrera</h5>
      </div>
      <div class="card-body d-flex">
        <div class="input-group mr-2 p-0">
          <div class="input-group-prepend">
            <span id="basic-addon1" class="input-group-text">
              <fa :icon="['far','filter']" />
            </span>
          </div>
          <input v-model="filter.text" type="text" class="form-control" placeholder="Namn" @keyup.enter="doFilter">
        </div>

        <select v-model="filter.status" class="form-control mr-2 col-2" @change="doFilter">
          <option value="">Filtrera på status</option>
          <option v-for="label in status.labels()" :key="label" :value="label">{{ $t("status_" + label) }}</option>
        </select>

        <select v-model="filter.auditor" class="form-control col-2" @change="doFilter">
          <option value="">Filtrera på granskare</option>
          <option v-for="user in auditors.filter(user => user.role > 1)" :key="user.id" :value="user.id">
            {{ user.name }} ({{ user.company.name }})
          </option>
        </select>

        <select v-model="filter.onlywithcomments" class="form-control col-2 ml-2" @change="doFilter">
          <option value="">Med och utan kommentarer</option>
          <option value="true">Endast med kommentar</option>
        </select>

        <select v-model="filter.printer" class="form-control col-2 ml-2" @change="doFilter">
          <option value="">Visa alla tryckeri</option>
          <option value="Mediapoolprint">Mediapoolprint</option>
          <option value="Mixiprint">Mixiprint</option>
        </select>

        <button class="btn btn-primary ml-2" @click="doFilter">Filtrera</button>
      </div>
    </div>

    <div v-if="view == 'list'" class="card" style="overflow-x: auto">
      <table-component
        ref="printables"
        :data="printables"
        :sort="sort"
        :show-filter="false"
        :loading="loading"
        table-class="table"
        filter-input-class="form-control"
        @changeSorting="sortBy"
        @rowClick="rowClick"
        @toggleChecked="toggleChecked"
      >
        <table-column :sortable="false" :label="'check-all'">
          <template slot-scope="item">
            <div class="form-check form-check-inline">
              <label @click.stop="">
                <input :value="item.id" v-model="checkedRows" type="checkbox" class="form-check-input" @click.stop="" @click="checkPropsForSelected">
              </label>
            </div>
          </template>
        </table-column>

        <table-column :sortable="false" label="">
          <template slot-scope="item">
            <printable-tools :printable="item" />
          </template>
        </table-column>

        <table-column show="name" label="Namn">
          <template slot-scope="item">
            <span :title="item.name">
              <HighlightText :keyword="filter.text" :sensitive="false" :over-write-style="{'color': '#212529', 'background-color':'yellow' }">{{ item.name }}</HighlightText>
            </span>
          </template>
        </table-column>

        <table-column show="status" label="Status">
          <template slot-scope="item">
            <fa :icon="status.icon(item.status)" fixed-width /> {{ $t('status_' + item.status) }}
          </template>
        </table-column>

        <table-column :sortable="false" show="comments_count" label="">
          <template slot-scope="item">
            <fa v-if="item.comments_count > 0" :icon="['fas','comments']" fixed-width />
          </template>
        </table-column>

        <table-column :sortable="false" show="client_auditors" label="Granskare hos kund">
          <template slot-scope="item">
            <b-badge v-for="auditor in item.client_auditors" :key="auditor.id" class="mr-1">
              {{ auditor.user.name }}
            </b-badge>
          </template>
        </table-column>

        <table-column :sortable="false" show="project.name" label="Projekt"/>
        <table-column :sortable="false" show="printable_type.name" label="Trycksakstyp"/>
        <table-column :sortable="true" show="printer" label="Tryckeri"/>
        <table-column show="deadline" label="Deadline"/>
        <table-column show="description" label="Övrigt"/>

        <table-column :sortable="false" show="tools-2" label="">
          <template v-if="canEdit" slot-scope="item">
            <div class="d-flex justify-content-between"
                 style="min-width: 3rem">

              <router-link :title="$t('edit')"
                           :to="{path : '/printables/' + item.id + '/edit'}"
                           class="text-primary">
                <fa :icon="['fas', 'pencil']"
                    fixed-width />
              </router-link>

              <a :title="$t('delete')"
                 href="#"
                 class="delete text-danger"
                 @click.stop="confirmDelete(item)">

                <fa :icon="['fas', 'trash']"
                    fixed-width />
              </a>
            </div>

          </template>
        </table-column>

      </table-component>
    </div>

    <div v-else class="row">
      <div v-for="printable in printables" v-if="printables && printables.length > 0" :key="printable.id" class="col-3">
        <b-card class="mb-4">

          <h6 slot="header" class="mb-0">
            <fa v-tooltip="$t('status_' + printable.status )" :icon="['fas','circle']" :class="'text-' + status.color(printable.status)" />
            <router-link :title="printable.name"
                         :to="{path : '/printables/' + printable.id, query: query }"
                         class="text-primary">
              {{ formatText(printable.name) }}
            </router-link>
          </h6>

          <router-link :title="$t('edit')"
                       :to="{path : '/printables/' + printable.id, query: query }"
                       class="preview-container w-100 d-flex justify-content-center">
            <img :src="getThumbnailPath(printable)" class="img-fluid preview-img">
          </router-link>

          <h6 slot="footer" class="mb-0">
            <printable-tools :printable="printable" />
          </h6>

        </b-card>
      </div>
      <div v-if="printables && printables.length == 0" class="col-12 text-center">
        <h2 class="h1 m-5 text-muted"><fa v-if="loading" :icon="['fas','circle-notch']" spin /> {{ loading ? 'Läser in artiklar...' : 'Inga artiklar' }}</h2>
      </div>
    </div>

    <div class="mt-4">
      <nav>
        <ul class="pagination justify-content-center">
          <li v-for="(item, index) in pagination.last_page" :key="index" :class="pagination.current_page == item ? 'active' : ''" class="page-item" >
            <a class="page-link" href="#" @click.prevent="paginate(item)">{{ item }}</a>
          </li>
        </ul>
      </nav>
    </div>

    <edit-deadline-modal :printable-ids="checkedRows" :deadline-modal="deadlineModal" @modal="hideDeadlineModal"/>
    <edit-auditor-modal :printable-ids="checkedRows" :company="currentCompany.id" :auditor-modal="auditorModal" @modal="hideAuditorModal"/>
    <edit-extern-auditor-modal :printable-ids="checkedRows" :company="currentCompany.id" :extern-auditor-modal="externAuditorModal" @modal="hideExternAuditorModal"/>
    <edit-export-modal :printable-ids="checkedRows" :export-modal="exportModal" @modal="hideExportModal"/>
    <edit-delivery-modal :printable-ids="checkedRows" :delivery-modal="deliveryModal" @modal="hideDeliveryModal"/>
    <printable-modal :printable-id="activePrintable" @clear="resetRoute" />
  </div>
</template>

<script>
import axios from 'axios'
import store from '~/store'
import swal from 'sweetalert2'
import HighlightText from 'vue-highlight-text'

import { TableComponent, TableColumn } from '~/components/table'

import EditAuditorModal from '~/components/EditAuditorModal'
import EditExternAuditorModal from '~/components/EditExternAuditorModal'
import EditDeadlineModal from '~/components/EditDeadlineModal'
import EditExportModal from '~/components/EditExportModal'
import EditDeliveryModal from '~/components/EditDeliveryModal'
import PrintableModal from '~/components/PrintableModal'
import PrintableTools from '~/components/PrintableTools'

export default {
  middleware: ['auth'],
  metaInfo () {
    return { title: this.$t('printables') }
  },
  components: {
    EditAuditorModal,
    EditExternAuditorModal,
    EditDeadlineModal,
    EditExportModal,
    EditDeliveryModal,
    PrintableModal,
    PrintableTools,
    TableComponent,
    TableColumn,
    HighlightText
  },
  data () {
    return {
      loading: true,
      view: 'list',
      page: '1',
      filter: {
        text: '',
        status: '',
        auditor: '',
        onlywithcomments: '',
        printer: ''
      },
      sort: {
        fieldName: 'updated_at',
        order: 'asc'
      },
      defaults: {
        page: '1',
        filter: {
          text: '',
          status: '',
          auditor: '',
          onlywithcomments: ''
        },
        sort: {
          fieldName: 'updated_at',
          order: 'asc'
        }
      },
      activePrintable: null,
      printables: [],
      title: null,
      pagination: {},
      auditors: [],
      checkedRows: [],
      creatingDownload: false,
      deadlineModal: false,
      auditorModal: false,
      externAuditorModal: false,
      exportModal: false,
      deliveryModal: false,
      disableExport: false,
      disableDelivery: false
    }
  },
  computed: {
    printable_type () {
      return this.$route.query.printable_type
        ? this.$route.query.printable_type
        : false
    },
    query () {
      return this.$route.query ? this.$route.query : false
    },
    params () {
      return this.$route.params
    },
    canEdit () {
      return store.getters['auth/user']
        ? store.getters['auth/user'].role >= 3
        : -1
    },
    currentCompany () {
      var company = store.getters['auth/company']
      return company
    }
  },
  watch: {
    $route: 'routeChanged'
  },
  created () {
    this.fetchAuditors()

    if (this.query.status) {
      this.filter.status = this.query.status
    }

    if (this.query.company_id) {
      this.activePrintable = this.params.id
      this.fetchData({
        page: '1'
      })
    } else {
      this.reRoute()
    }
  },
  methods: {
    checkPropsForSelected () {
      this.$nextTick(() => {
        var printables = []
        var printables = this.printables.filter((print) => this.checkedRows.includes(print.id))

        this.disableExport = false
        this.disableDelivery = false

        printables.forEach(printable => {
          console.log(printable.status)
          if (printable.status != 'approved') {
            this.disableExport = true
          }

          if (printable.exported_at == null) {
            this.disableDelivery = true
          }
        })
      })
    },
    openDeadlineModal () {
      this.deadlineModal = true
    },
    openAuditorModal () {
      this.auditorModal = true
    },
    openExternAuditorModal () {
      this.externAuditorModal = true
    },
    openExportModal () {
      this.exportModal = true
    },
    openDeliveryModal () {
      this.deliveryModal = true
    },
    hideDeadlineModal (reload) {
      this.deadlineModal = false
      if (reload) {
        location.reload()
      }
    },
    hideAuditorModal (reload) {
      this.auditorModal = false

      if (reload) {
        location.reload()
      }
    },
    hideExternAuditorModal (reload) {
      this.externAuditorModal = false

      if (reload) {
        location.reload()
      }
    },
    hideExportModal (reload) {
      this.exportModal = false

      if (reload) {
        location.reload()
      }
    },
    hideDeliveryModal (reload) {
      this.deliveryModal = false

      if (reload) {
        location.reload()
      }
    },
    toggleChecked (e) {
      if (this.checkedRows.length < this.printables.length) {
        this.checkedRows = this.printables.map(item => item.id)
      } else {
        this.checkedRows = []
      }
    },
    refresh () {
      this.page = '1',
      this.filter = this.defaults.filter,
      this.sort = {
        fieldName: 'updated_at',
        order: 'asc'
      }
    },
    paginate (page) {
      this.page = page
      this.fetchData()
    },
    doFilter () {
      this.page = 1
      this.fetchData()
    },
    reRoute () {
      this.$router.push({
        path: '/printables?company_id=' + this.currentCompany.id,
        query: this.query
      })
    },
    resetRoute () {
      this.activePrintable = null

      this.$router.push({
        path: '/printables',
        query: this.query
      })
    },
    routeChanged (newRoute, oldRoute) {
      if (
        (newRoute.query.type !== oldRoute.query.type &&
        newRoute.name !== 'printables.id')
      ) {
        this.printables = []
        this.refresh()
        this.fetchData()
      }

      if (newRoute.query.force_refresh) {
        this.printables = []
        this.refresh()
        this.fetchData()

        let query = Object.assign({}, this.$route.query)
        delete query.force_refresh
        this.$router.replace({ query })
      }

      if (newRoute.params.id !== oldRoute.params.id) {
        this.activePrintable = newRoute.params.id
      }
    },
    rowClick (entry) {
      this.$router.push({
        path: '/printables/' + entry.data.id,
        query: this.query
      })
      this.checkPropsForSelected()
    },
    formatText (str) {
      if (str.length > 30) {
        return (
          str.substr(0, 15) + '...' + str.substr(str.length - 10, str.length)
        )
      }
      return str
    },
    getThumbnailPath (printable) {
      if (printable.printable_pages[0]) {
        return (
          printable.printable_pages[0].attachment_page.prefix +
          printable.printable_pages[0].attachment_page.path
        )
      }

      return ''
    },
    getQuery () {
      var q = this.query

      let string = Object.keys(q)
        .map(function (k) {
          if (q[k]) {
            return encodeURIComponent(k) + '=' + encodeURIComponent(q[k])
          }
        })
        .join('&')

      return string
    },
    sortBy (col) {
      let sortField = col.sortBy
      let order = sortField !== col.sortBy ? 'asc' : (this.sort.order == 'asc' ? 'desc' : 'asc')

      this.sort = {
        fieldName: sortField,
        order: order
      }

      this.fetchData()
    },
    async makeExport (is_preview) {
      this.creatingDownload = true
      const response = await axios.get('/api/export?printables=' + this.checkedRows.join(',') + '&type=' + (is_preview ? 'preview' : 'full'))
      this.creatingDownload = false
      window.location = response.data
    },
    async confirmDelete (entry) {
      swal({
        title: this.$t('delete') + ' &quot;' + entry.name + '&quot;?',
        text: this.$t('cant_undo'),
        type: 'error',
        reverseButtons: true,
        showCancelButton: true,
        confirmButtonText: this.$t('delete'),
        confirmButtonClass: 'bg-danger',
        cancelButtonText: this.$t('cancel')
      }).then(result => {
        if (result.value) {
          this.deleteEntry(entry)
        }
      })
    },
    async deleteEntry (entry) {
      if (entry.id) {
        const { data } = await axios.delete('/api/printable/' + entry.id)
        this.fetchData()
      }
    },
    async fetchData () {
      this.loading = true
      this.printables = []

      let params = {
        ...this.query,
        ...{
          page: this.page,
          filter: this.filter.text,
          filter_auditor: this.filter.auditor,
          status: this.filter.status,
          sortby: this.sort.fieldName,
          order: this.sort.order,
          onlywithcomments: this.filter.onlywithcomments,
          printer: this.filter.printer
        }
      }

      const response = await axios.get('/api/printable', { params: params })

      let pagination = response.data.pagination
      let data = pagination.data

      delete pagination.data

      this.$router.push({
        query: params
      })

      this.title = response.data.title
      this.pagination = pagination
      this.printables = data
      this.loading = false
    },
    async fetchAuditors () {
      const { data } = await axios.get('/api/user', {
        params: {
          company_id: this.query.company_id
        }
      })

      this.auditors = data
    }
  }
}
</script>

<style lang="scss">
.preview-container {
  background: rgba(255, 255, 255, 0.2);

  &:hover {
    background: rgba(255, 255, 255, 0.5);
  }
}

.preview-img {
  max-height: 160px;
  min-height: 160px;
}

td:first-child {
  width: 1em;
  padding: 0;

  label {
    padding: .9rem;
  }

  .form-check {
    margin: 0;
  }
}

.admin{
  td:nth-child(2) {
    width: 130px;
  }

  td:nth-child(3) {
    max-width: 280px;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  td:last-child {
    width: 70px;
  }
}
.user {
  /*td:first-child, th:first-child {
    display: none;
  }*/

  td:nth-child(2) {
    width: 90px;
  }
}

li.disabled,
.table-component__table__caption {
  display: none;
}

.pagination li {
  display: none;

  &.page-item {
    display: list-item;
  }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/pages/printables/index.vue